import { API_BASE } from '../config/apiBase';

export type EventStatus = 'Active' | 'Closed';

export interface EventCollaborator {
  user_id: number;
  username: string;
}

export interface EventRow {
  id: number;
  project_id: number;
  name: string;
  location: string | null;
  start_date: string | null;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  status: EventStatus;
  created_by_user_id: number | null;
  created_by_username: string | null;
  created_at: string;
  collaborators: EventCollaborator[];
}

export interface EventsListResponse {
  active: EventRow[];
  past: EventRow[];
  void: EventRow[];
}

export type IntakeFieldType = 'text' | 'email' | 'textarea' | 'number' | 'phone' | 'date' | 'select';

export interface IntakeField {
  id: number;
  label: string;
  field_type: IntakeFieldType;
  required: boolean;
  options: string[];
  sort_order: number;
}

export interface IntakeForm {
  id: number;
  title: string;
  description: string | null;
}

export interface IntakeResponse {
  id: number;
  answers: Record<string, string>;
  created_at: string;
}

export const INTAKE_FIELD_TYPE_LABELS: Record<IntakeFieldType, string> = {
  text: 'Short text',
  email: 'Email',
  textarea: 'Long text',
  number: 'Number',
  phone: 'Phone',
  date: 'Date',
  select: 'Dropdown',
};

async function parse<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || fallback);
  return data as T;
}

export async function fetchEvents(userId: number): Promise<EventsListResponse> {
  const res = await fetch(`${API_BASE}/api/events?userId=${encodeURIComponent(userId)}`);
  const data = await parse<Partial<EventsListResponse>>(res, 'Failed to load events.');
  return { active: data.active ?? [], past: data.past ?? [], void: data.void ?? [] };
}

export async function fetchEvent(eventId: number, viewerUserId: number): Promise<EventRow> {
  const res = await fetch(
    `${API_BASE}/api/events/${encodeURIComponent(eventId)}?viewerUserId=${encodeURIComponent(viewerUserId)}`
  );
  return parse<EventRow>(res, 'Failed to load event.');
}

export async function createEvent(payload: {
  user_id: number;
  name: string;
  location?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
}): Promise<EventRow> {
  const res = await fetch(`${API_BASE}/api/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parse<EventRow>(res, 'Failed to create event.');
}

export async function updateEvent(
  eventId: number,
  payload: {
    user_id: number;
    name?: string;
    location?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    start_time?: string | null;
    end_time?: string | null;
    status?: EventStatus;
    collaborator_user_ids?: number[];
  }
): Promise<EventRow> {
  const res = await fetch(`${API_BASE}/api/events/${encodeURIComponent(eventId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parse<EventRow>(res, 'Failed to update event.');
}

export async function fetchIntakeForm(
  eventId: number,
  viewerUserId: number
): Promise<{ form: IntakeForm; fields: IntakeField[] }> {
  const res = await fetch(
    `${API_BASE}/api/events/${encodeURIComponent(eventId)}/intake-form?viewerUserId=${encodeURIComponent(viewerUserId)}`
  );
  return parse<{ form: IntakeForm; fields: IntakeField[] }>(res, 'Failed to load intake form.');
}

export async function updateIntakeForm(
  eventId: number,
  payload: { user_id: number; title?: string; description?: string | null }
): Promise<IntakeForm> {
  const res = await fetch(`${API_BASE}/api/events/${encodeURIComponent(eventId)}/intake-form`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parse<IntakeForm>(res, 'Failed to update form.');
}

export async function createIntakeField(
  eventId: number,
  payload: { user_id: number; label: string; field_type: IntakeFieldType; required: boolean; options?: string[] }
): Promise<IntakeField> {
  const res = await fetch(`${API_BASE}/api/events/${encodeURIComponent(eventId)}/intake-form/fields`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parse<IntakeField>(res, 'Failed to add field.');
}

export async function updateIntakeField(
  eventId: number,
  fieldId: number,
  payload: {
    user_id: number;
    label?: string;
    field_type?: IntakeFieldType;
    required?: boolean;
    options?: string[];
    sort_order?: number;
  }
): Promise<IntakeField> {
  const res = await fetch(
    `${API_BASE}/api/events/${encodeURIComponent(eventId)}/intake-form/fields/${encodeURIComponent(fieldId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  return parse<IntakeField>(res, 'Failed to update field.');
}

export async function deleteIntakeField(eventId: number, fieldId: number, userId: number): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/events/${encodeURIComponent(eventId)}/intake-form/fields/${encodeURIComponent(fieldId)}?user_id=${encodeURIComponent(userId)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || 'Failed to delete field.');
  }
}

export async function fetchIntakeResponses(eventId: number, viewerUserId: number): Promise<IntakeResponse[]> {
  const res = await fetch(
    `${API_BASE}/api/events/${encodeURIComponent(eventId)}/intake-form/responses?viewerUserId=${encodeURIComponent(viewerUserId)}`
  );
  const data = await parse<{ responses?: IntakeResponse[] }>(res, 'Failed to load responses.');
  return data.responses ?? [];
}

export async function createIntakeResponse(
  eventId: number,
  answers: Record<string, string>
): Promise<IntakeResponse> {
  const res = await fetch(`${API_BASE}/api/events/${encodeURIComponent(eventId)}/intake-form/responses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers }),
  });
  return parse<IntakeResponse>(res, 'Failed to save response.');
}
